<template>
<div>
    <b-modal
      id="gift-card-appreciation-modal"
      ref="gift-card-appreciation-modal"
      hide-footer
      hide-header
      no-close-on-backdrop
      modal-backdrop
      centered
      size="lg"
      @hidden="onModalHidden"
    >
        <div class="text-center" style="font-family: 'Open Sans', sans-serif;">
            <!-- Header with Icon -->
            <div class="modal-icon-container">
                <div class="modal-icon-circle">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#179346" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                    </svg>
                </div>
            </div>

            <!-- Title -->
            <div class="modal-text-content">
                <h3 class="modal-title">Appreciation Messages</h3>
                <p class="modal-subtitle" v-if="!isLoading && appreciationList.length > 0">
                    You've received {{ totalMessages }} thank you {{ totalMessages === 1 ? 'message' : 'messages' }}!
                </p>
            </div>

            <!-- Loading State -->
            <div v-if="isLoading" class="loading-container">
                <div class="loading-spinner"></div>
                <p class="loading-text">Loading your messages...</p>
            </div>

            <!-- Messages List -->
            <div v-else-if="appreciationList.length > 0" class="messages-container">
                <div class="messages-list">
                    <div
                        v-for="(appreciation, index) in appreciationList"
                        :key="appreciation.id || index"
                        class="message-card"
                    >
                        <!-- Sender Info -->
                        <div class="sender-info">
                            <div class="sender-name">{{ appreciation.first_name }} {{ appreciation.last_name }}</div>
                        </div>

                        <!-- Gift Card Amount -->
                        <div class="gift-card-amount">${{ appreciation.amount }}</div>

                        <!-- Thank You Message -->
                        <div class="thank-you-message">
                            "Thank you so much for the gift card!"
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="hasMorePages" class="load-more-container">
                    <button
                        class="load-more-btn"
                        @click="loadMoreMessages"
                        :disabled="isLoadingMore"
                    >
                        {{ isLoadingMore ? 'Loading...' : 'Load More' }}
                    </button>
                </div>

                <!-- Pagination Info -->
                <div class="pagination-info">
                    Showing {{ fromItem }} to {{ toItem }} of {{ totalMessages }} messages
                </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state">
                <p class="empty-text">
                    No appreciation messages yet.<br>
                    When people thank you for gift cards, they'll appear here!
                </p>
            </div>

            <!-- OK Button -->
            <button class="modal-ok-button" @click="closeModal">
                OK
            </button>
        </div>
    </b-modal>
</div>
</template>

<script>
import api from "../../../api/giftcard.js";

export default {
    name: 'GiftCardAppreciationModal',
    props:{
      alertCompletion:{
        type:Function
      }
    },
    data() {
        return {
            appreciationList: [],
            isLoading: false,
            isLoadingMore: false,
            currentPage: 1,
            perPage: 10,
            totalMessages: 0,
            lastPage: 1,
            hasMorePages: false,
            fromItem: 0,
            toItem: 0,
            errorMessage: ''
        }
    },
    methods: {
        async showModal() {
            if (this.$refs['gift-card-appreciation-modal']) {
                this.$refs['gift-card-appreciation-modal'].show();
                await this.fetchAppreciationList();
            }
        },

        closeModal() {
            if (this.$refs['gift-card-appreciation-modal']) {
                this.alertCompletion();
                this.$refs['gift-card-appreciation-modal'].hide();
            }
        },

        onModalHidden() {
            // Reset state when modal is closed
            this.appreciationList = [];
            this.currentPage = 1;
            this.totalMessages = 0;
            this.hasMorePages = false;
            this.isLoading = false;
            this.isLoadingMore = false;
            this.fromItem = 0;
            this.toItem = 0;
        },

        async fetchAppreciationList(page = 1, append = false) {
            if (append) {
                this.isLoadingMore = true;
            } else {
                this.isLoading = true;
                this.appreciationList = [];
                this.currentPage = 1;
            }

            try {
                // Get current user ID from localStorage
                const currentUser = localStorage.getItem("consumer_login_response")
                    ? JSON.parse(localStorage.getItem("consumer_login_response"))
                    : null;

                if (!currentUser || !currentUser.user_id) {
                    this.errorMessage = 'User not found';
                    return;
                }

                const response = await api.fetchAppreciationlist({
                    page: page,
                    per_page: this.perPage,
                    from: currentUser.user_id // This will mark messages as read
                });

                if (response.code === 200) {
                    const responseData = response.data;
                    const newMessages = responseData.data || [];

                    if (append) {
                        this.appreciationList = [...this.appreciationList, ...newMessages];
                    } else {
                        this.appreciationList = newMessages;
                    }

                    // Update pagination info from server response
                    this.currentPage = responseData.current_page || 1;
                    this.totalMessages = responseData.total || 0;
                    this.lastPage = responseData.last_page || 1;
                    this.hasMorePages = responseData.has_more_pages || false;
                    this.fromItem = responseData.from || 0;
                    this.toItem = responseData.to || 0;

                } else {
                    this.errorMessage = response.message || 'Failed to load messages';
                }
            } catch (error) {
                console.error('Error fetching appreciation messages:', error);
                this.errorMessage = 'Failed to load messages';
            } finally {
                this.isLoading = false;
                this.isLoadingMore = false;
            }
        },

        async loadMoreMessages() {
            if (!this.hasMorePages || this.isLoadingMore) return;

            const nextPage = this.currentPage + 1;
            await this.fetchAppreciationList(nextPage, true);
        }
    }
}
</script>

<style scoped>
/* Use existing modal styles from the project */
.modal-icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
}

.modal-icon-circle {
  width: 115px;
  height: 115px;
  background-color: #f0f0f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.modal-text-content {
  margin-bottom: 30px;
}

.modal-title {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 10px;
  font-family: 'Open Sans', sans-serif;
}

.modal-subtitle {
  font-size: 16px;
  color: #666666;
  margin-bottom: 0;
  font-family: 'Open Sans', sans-serif;
}

.loading-container {
  margin: 20px 0;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #179346;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 14px;
  color: #666666;
  margin: 0;
}

.messages-container {
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 20px;
}

.messages-list {
  text-align: left;
}

.message-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
  border-left: 4px solid #179346;
}

.sender-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.sender-name {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
}

.gift-card-amount {
  font-size: 18px;
  font-weight: 700;
  color: #179346;
  margin-bottom: 10px;
}

.thank-you-message {
  font-size: 14px;
  color: #333333;
  font-style: italic;
  line-height: 1.4;
}

.load-more-container {
  margin-bottom: 20px;
}

.load-more-btn {
  background-color: #179346;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.load-more-btn:hover:not(:disabled) {
  background-color: #0f7a3a;
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: #666666;
  margin-bottom: 20px;
}

.empty-state {
  margin: 20px 0;
}

.empty-text {
  font-size: 16px;
  color: #666666;
  line-height: 1.5;
  margin: 0;
}

.modal-ok-button {
  background-color: #000000;
  color: #ffffff;
  border: none;
  padding: 15px 40px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  max-width: 200px;
  transition: background-color 0.2s ease;
  font-family: 'Open Sans', sans-serif;
}

.modal-ok-button:hover {
  background-color: #333333;
}

/* Mobile responsive styles */
@media (max-width: 767px) {
  .modal-icon-circle {
    width: 101px;
    height: 101px;
  }

  .modal-title {
    font-size: 18px;
  }

  .modal-subtitle {
    font-size: 14px;
  }

  .message-card {
    padding: 12px;
  }

  .sender-name {
    font-size: 14px;
  }

  .gift-card-amount {
    font-size: 16px;
  }

  .thank-you-message {
    font-size: 13px;
  }
}

/* Custom scrollbar for messages container */
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>